import React, { useEffect, useRef, useState, useContext } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { AppStateContext } from "../../state/AppProvider";

type CustomModalProps = {
    headerText?: string;
    buttonLabel?: string;
    onConfirm?(): void;
    onCancel?(): void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    children?: React.ReactElement | null;
    modal_id: string;
    showAcceptButton?: boolean;
};

export const CustomModal: React.FunctionComponent<CustomModalProps> = ({
    headerText,
    buttonLabel,
    onConfirm,
    onCancel,
    isOpen,
    setIsOpen,
    children,
    modal_id,
    showAcceptButton,
}: CustomModalProps) => {
    const modalRef = useRef<HTMLDialogElement>(null);
    const showButton = showAcceptButton ?? true;

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        setIsOpen(false);
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
        setIsOpen(false);
    };

    useEffect(() => {
        const dialogElement = modalRef.current;
        if (dialogElement !== null) {
            dialogElement.addEventListener("cancel", () => {
                setIsOpen(false);
            });

            return () => {
                if (dialogElement !== null) {
                    dialogElement.removeEventListener("cancel", () => {
                        setIsOpen(false);
                    });
                }
            };
        }
    }, [setIsOpen]);

    useEffect(() => {
        if (modalRef.current !== null) {
            if (isOpen) {
                modalRef.current.showModal();
                // fix for safari bug, where invisible modal disables scrolling on the page
                modalRef.current.classList.remove("!h-0");
            } else {
                modalRef.current.close();
                modalRef.current.classList.add("!h-0");
            }
        }
    }, [isOpen, setIsOpen]);

    return (
        <dialog ref={modalRef} id={modal_id} className="modal !overflow-y-auto !h-0">
            <div className="modal-box bg-base !border-2 !border-brightpink !max-h-screen">
                <form method="dialog">
                    {onCancel && (
                        <button
                            className="btn btn-xl btn-circle btn-link btn-ghost absolute right-4 top-4"
                            onClick={handleCancel}
                        >
                            <IoCloseOutline size={42} color="#720064" />
                        </button>
                    )}
                </form>
                <h3 className="text-black text-modal-header font-modal-header truncate pb-4">
                    {headerText ?? "Default Header"}
                </h3>
                {children !== undefined ? children : <p>No content specified.</p>}
                {onConfirm && showButton && (
                    <div className="modal_action flex justify-end pt-4">
                        <button
                            className="btn btn-primary !pl-6 !pr-6 !rounded-3xl !h-8 !min-h-8 !w-152px !min-w-152px text-button-text font-button-text"
                            onClick={handleConfirm}
                        >
                            {buttonLabel ?? "speichern"}
                        </button>
                    </div>
                )}
            </div>
        </dialog>
    );
};

export default CustomModal;

type ConfirmModalPromp = {
    headerText?: string;
    onConfirm(): void;
    onCancel(): void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    modal_id: string;
};

export const ConfirmModal: React.FunctionComponent<ConfirmModalPromp> = ({
    headerText,
    onConfirm,
    onCancel,
    isOpen,
    setIsOpen,
    modal_id,
}: ConfirmModalPromp) => {
    return (
        <CustomModal
            headerText={headerText}
            buttonLabel="Bestätigen"
            onConfirm={onConfirm}
            onCancel={onCancel}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            modal_id={modal_id}
        >
            <p className="pt-5 text-button-text font-button-text text-black">Sicher löschen?</p>
        </CustomModal>
    );
};

export const ErrorModal: React.FunctionComponent = () => {
    const appStateContext = useContext(AppStateContext);
    const modalRef = useRef<HTMLDialogElement>(null);
    const modal_id = "error-modal";
    const [isOpen, setIsOpen] = useState(false);
    const [subHeaderText, setSubHeaderText] = useState("");
    const [errMsg, setErrMsg] = useState("");

    useEffect(() => {
        const dialogElement = modalRef.current;
        if (dialogElement !== null) {
            dialogElement.addEventListener("cancel", () => {
                setIsOpen(false);
            });

            return () => {
                if (dialogElement !== null) {
                    dialogElement.removeEventListener("cancel", () => {
                        setIsOpen(false);
                    });
                }
            };
        }
    }, [setIsOpen]);

    useEffect(() => {
        if (modalRef.current !== null) {
            if (isOpen) {
                modalRef.current.showModal();
            } else {
                modalRef.current.close();
            }
        }
    }, [isOpen, setIsOpen]);

    useEffect(() => {
        if (appStateContext?.state.errorMessage) {
            setIsOpen(true);
            setErrMsg(appStateContext?.state.errorMessage.message);
            if (appStateContext?.state.errorMessage.subtitle) {
                setSubHeaderText(appStateContext?.state.errorMessage.subtitle);
            }
        }
    }, [appStateContext?.state.errorMessage]);

    const handleClose = () => {
        setIsOpen(false);
        appStateContext?.dispatch({ type: "SET_ERROR", payload: null });
    };

    return (
        <dialog ref={modalRef} id={modal_id} className="modal !overflow-visible !overflow-y-auto">
            <div className="modal-box !bg-brightpink !border-2 !border-brightpink">
                <h3 className="text-modal-header font-modal-header text-black  truncate">Das hat nicht geklappt...</h3>
                {subHeaderText && (
                    <p className="pt-4 text-black font-container-subheader text-container-subheader">{subHeaderText}</p>
                )}
                <p className="pt-4 text-black font-chat-text text-chat-text">{errMsg}</p>
                <div className="modal_action flex justify-end pt-4">
                    <button
                        className="btn !bg-white !text-black !pl-6 !pr-6 !rounded-3xl !h-8 !min-h-8 !w-152px !min-w-152px text-button-text font-button-text"
                        onClick={handleClose}
                    >
                        OK
                    </button>
                </div>
            </div>
        </dialog>
    );
};
